import { getItemThumbnail, transformItemPhotoDtos } from '@marketplace-web/shared/item-photo-data'
import { UserDto } from '@marketplace-web/shared/user-data'
import { transformOfferDto } from '@marketplace-web/offers/price-offers-data'
import { transformUserBundleDiscountDto } from '@marketplace-web/bundles/items-bundle-data'
import { transformPackageSizeDto } from '@marketplace-web/shipping/package-size-data'
import { transformCurrencyAmountDto } from '@marketplace-web/domain/currency-data'
import { transformAddress } from '@marketplace-web/address/address-data'

import { transformShipmentDto } from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import {
  TransactionDto,
  TransactionItemDto,
  TransactionItemModel,
  TransactionModel,
  TransactionOrderDto,
  TransactionOrderModel,
  TransactionUserModel,
} from '../types/transaction'
import { AdditionalTransactionAction } from '../constants'

const transformSeller = (seller: UserDto): TransactionUserModel => ({
  id: seller.id,
  takenItemCount: seller.taken_item_count,
  defaultAddress: seller.default_address && transformAddress(seller.default_address),
  bundleDiscount: seller.bundle_discount
    ? transformUserBundleDiscountDto(seller.bundle_discount)
    : null,
  countryCode: seller.country_code,
  business: seller.business,
  closetCountdownEndDate: seller.closet_countdown_end_date,
  login: seller.login,
})

const transformTransactionItemDto = (item: TransactionItemDto): TransactionItemModel => ({
  id: item.id,
  title: item.title,
  size: item.size_title || '',
  status: item.status,
  price: transformCurrencyAmountDto(item.price),
  thumbnailUrl: getItemThumbnail(item.photos),
  url: item.url,
  photos: transformItemPhotoDtos(item.photos),
})

export const transformTransactionItemDtos = (
  items: Array<TransactionItemDto>,
): Array<TransactionItemModel> => items.map(transformTransactionItemDto)

export const transformOrder = (order: TransactionOrderDto): TransactionOrderModel => ({
  items: transformTransactionItemDtos(order.items),
  itemIds: order.item_ids,
  title: order.title,
  reserved: order.reserved,
  packageSize: order.package_size ? transformPackageSizeDto(order.package_size) : null,
})

const transformBuyer = (buyer: UserDto): TransactionUserModel => ({
  id: buyer.id,
  takenItemCount: buyer.taken_item_count,
  bundleDiscount: buyer.bundle_discount
    ? transformUserBundleDiscountDto(buyer.bundle_discount)
    : null,
  countryCode: buyer.country_code,
  business: buyer.business,
  defaultAddress: buyer.default_address && transformAddress(buyer.default_address),
  login: buyer.login,
})

export const transformTransaction = ({
  id,
  buyer,
  order,
  shipment,
  available_actions,
  debit_processed_at,
  user_msg_thread_id,
  items_count,
  item_id,
  offer,
  status,
  seller,
  status_title,
  user_side,
  buyer_phone_number,
  seller_phone_number,
  item_title,
  suspending_complaint_id,
  offline_verification,
  total_amount_without_tax,
  service_fee,
}: TransactionDto): TransactionModel => ({
  id,
  order: transformOrder(order),
  buyer: transformBuyer(buyer),
  userSide: user_side,
  shipment: shipment && transformShipmentDto(shipment),
  canMarkAsReceived: available_actions.includes(AdditionalTransactionAction.MarkAsReceived),
  isDebitProcessed: !!debit_processed_at,
  conversationId: user_msg_thread_id,
  availableActions: available_actions,
  itemsCount: items_count,
  itemId: item_id,
  offer: transformOfferDto(offer),
  status,
  seller: seller ? transformSeller(seller) : null,
  statusTitle: status_title,
  buyerPhoneNumber: buyer_phone_number,
  sellerPhoneNumber: seller_phone_number,
  itemTitle: item_title,
  complaintId: suspending_complaint_id ? suspending_complaint_id.toString() : null,
  offlineVerification: offline_verification,
  totalAmountWithoutTax: transformCurrencyAmountDto(total_amount_without_tax),
  serviceFee: transformCurrencyAmountDto(service_fee),
})

export const transformTransactions = (
  transactions: Array<TransactionDto>,
): Array<TransactionModel> => transactions.map(transformTransaction)
